import { ChartSpline, Info, RotateCcw, TriangleAlert } from 'lucide-react';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useUnit } from 'effector-react';
import { $isSimulating, $result } from '../../entities/simulation';
import { VIEWS, visibleViews, type View } from '../../entities/view-settings';
import { cn } from '../../shared/lib/cn';
import {
  Button,
  EmptyState,
  ErrorBoundary,
  TabsContent,
  TabsList,
  TabsTrigger,
  Tooltip,
} from '../../shared/ui';
import { Balance } from './ui/balance';
import { Compare } from './ui/compare';
import { CashFlow } from './ui/cash-flow';
import { Debt } from './ui/debt';
import { ExplainDialog } from './ui/explain-dialog';
import { Ledger } from './ui/ledger';
import { Overview } from './ui/overview';
import { RangeBar } from './ui/range-bar';
import { Returns } from './ui/returns';
import { Risk } from './ui/risk';
import { useCanvasData, type CanvasData } from './ui/use-canvas';

/** Views that use Monte Carlo results; the others always show the average (deterministic) path. */
const MC_VIEWS: ReadonlySet<View> = new Set(['overview', 'risk', 'compare']);

export function ChartCanvas() {
  const { t } = useTranslation();
  const data = useCanvasData();
  const [simulating, result] = useUnit([$isSimulating, $result]);

  // Performance budget instrumentation: edit committed → chart painted (docs/PLAN.md §1).
  useEffect(() => {
    if (performance.getEntriesByName('finsim:edit', 'mark').length === 0) return;
    performance.measure('finsim:edit→chart', 'finsim:edit');
    performance.clearMarks('finsim:edit');
  }, [result]);

  return (
    // Only the view scrolls: the tab bar and the time-range bar stay outside the scroll area,
    // so they can never cover content or a focused control (WCAG 2.2 "Focus Not Obscured").
    <main data-tour="charts" className="flex h-full min-w-0 flex-col bg-bg-0">
      <div className="z-20 flex h-10 shrink-0 items-center border-b border-border-subtle bg-bg-0 px-3">
        <TabsList aria-label={t('views.label')} className="overflow-x-auto">
          {visibleViews(data?.project ?? null).map((view, index) => (
            <Tooltip key={view} content={t(`views.${view}`)} shortcut={[String(index + 1)]}>
              <TabsTrigger value={view}>{t(`views.${view}`)}</TabsTrigger>
            </Tooltip>
          ))}
        </TabsList>
      </div>
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {VIEWS.map((view) => (
          <TabsContent key={view} value={view} className="flex min-h-0 flex-1 flex-col">
            <h1 className="sr-only">{t(`views.${view}`)}</h1>
            {data ? (
              <div
                className={cn(
                  'flex min-h-0 flex-1 flex-col transition-opacity duration-150',
                  (simulating || (data.mcPending && MC_VIEWS.has(view))) && 'opacity-70 delay-300',
                )}
                aria-busy={simulating || (data.mcPending && MC_VIEWS.has(view)) || undefined}
              >
                {data.simMode === 'monteCarlo' && !MC_VIEWS.has(view) && (
                  <p
                    role="note"
                    className="mx-4 mt-4 flex items-start gap-2 rounded-md border border-border-subtle bg-bg-1 px-3 py-2 text-xs text-text-2"
                  >
                    <Info className="mt-px size-3.5 shrink-0 text-text-3" aria-hidden />
                    {t('mc.averagePathNote')}
                  </p>
                )}
                <ErrorBoundary
                  resetKeys={[view, data.result, data.mc]}
                  fallback={(reset) => (
                    <EmptyState
                      icon={<TriangleAlert />}
                      title={t('errors.viewFailed')}
                      action={
                        <Button leadingIcon={<RotateCcw />} onClick={reset}>
                          {t('errors.tryAgain')}
                        </Button>
                      }
                      className="flex-1 py-24"
                    />
                  )}
                >
                  <ViewContent view={view} data={data} />
                </ErrorBoundary>
              </div>
            ) : (
              <EmptyState
                icon={<ChartSpline />}
                title={t('charts.netWorth')}
                body={t('charts.noData')}
                className="flex-1 py-24"
              />
            )}
          </TabsContent>
        ))}
      </div>
      {data && <RangeBar data={data} />}
      {data && <ExplainDialog data={data} />}
    </main>
  );
}

function ViewContent({ view, data }: { view: View; data: CanvasData }) {
  switch (view) {
    case 'overview':
      return <Overview data={data} />;
    case 'cashFlow':
      return <CashFlow data={data} />;
    case 'balance':
      return <Balance data={data} />;
    case 'debt':
      return <Debt data={data} />;
    case 'ledger':
      return <Ledger data={data} />;
    case 'risk':
      return <Risk data={data} />;
    case 'compare':
      return <Compare data={data} />;
    case 'returns':
      return <Returns data={data} />;
    default:
      return null;
  }
}
